import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { UserMenus } from "./types";
import clsx from "clsx";

type RewardDetailsProps = {
  userData: UserMenus;
  isDesktop: boolean;
};

const arrowStyle = {
  up: "-rotate-180",
  down: "rotate-0",
};

const UserRewardDetails = ({ isDesktop, userData }: RewardDetailsProps) => {
  const [arrowState, setArrowState] = useState<keyof typeof arrowStyle>("down");
  const [isUserDetailsPopupOpen, setIsUserDetailsPopupOpen] = useState(false);
  const doesUserNotHaveFreeTickets = userData?.FreeTicketNotification.AllAvailableFreeTickets === 0;
  const ref = useRef<HTMLDivElement>();
  useEffect(() => {
    document.addEventListener("click", ({ target }: MouseEvent) => {
      if (!ref.current?.contains(target as Node)) {
        setArrowState("down");
        setIsUserDetailsPopupOpen(false);
      }
    });
  }, []);
  const onFreeTicketsClick = () => {
    if (doesUserNotHaveFreeTickets) {
      return;
    }
    location.href = "/account/my-wallet";
  };

  const getUserGameCreditsAndFreeTickets = () => (
    <div
      style={{
        top: `${ref.current?.clientHeight}px`,
        width: `${ref.current?.clientWidth + 35}px`,
      }}
      className={`bg-white p-2 mt-1 rounded-lg left-0 flex gap-3 flex-col absolute border border-solid border-[#c7c7c7] z-[1000]`}>
      <div className="flex justify-between">
        <div className="flex flex-col gap-2">
          <span className="text-[#28282b] font-jost text-[16px]">Hello {userData?.FirstName},</span>
          <span className="text-[#696971] text-[11px]">You have:</span>
        </div>
        {userData?.TierImageUrl && (
          <img src={userData?.TierImageUrl} alt="user-loyalty-tier-image" width={25} height={25} />
        )}
      </div>
      <hr />
      <div className="flex gap-2">
        <img src="/assets/images/money-icon.svg" alt="money-icon" width={16} height={16} />
        <span className="text-[12px] text-[#454553]">Game Credit:</span>
        <span className="text-[12px] text-[#ff8200]">{userData?.UserDetails.FormattedTotalGameCredits}</span>
      </div>
      {userData?.FreeTicketNotification !== null && userData?.FreeTicketNotification.AllAvailableFreeTickets > 0 && (
        <div className="flex gap-2 cursor-pointer hover:underline" onClick={onFreeTicketsClick}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M15.7992 6.3104V3.2C15.7992 2.537 15.2622 2 14.5992 2H1.39922C0.736219 2 0.199219 2.537 0.199219 3.2V6.3104C0.897019 6.5582 1.39922 7.2176 1.39922 8C1.39922 8.7824 0.897019 9.4418 0.199219 9.6896V12.8C0.199219 13.463 0.736219 14 1.39922 14H14.5992C15.2622 14 15.7992 13.463 15.7992 12.8V9.6896C15.1014 9.4418 14.5992 8.7824 14.5992 8C14.5992 7.2176 15.1014 6.5582 15.7992 6.3104ZM11.3304 7.3766L9.58922 8.6726L10.236 10.7444C10.323 11.0222 10.0074 11.2514 9.76982 11.0828L7.99922 9.8276L6.22862 11.0828C5.99102 11.2514 5.67602 11.0222 5.76242 10.7444L6.40922 8.6726L4.66802 7.3766C4.43462 7.2032 4.55522 6.8324 4.84622 6.8288L7.01642 6.8036L7.71122 4.7474C7.80422 4.472 8.19422 4.472 8.28722 4.7474L8.98202 6.8036L11.1522 6.8288C11.4432 6.8318 11.5638 7.2026 11.3304 7.3766Z"
              fill="#FF8200"
            />
          </svg>
          <span className="text-[12px] text-[#454553]">
            {userData?.FreeTicketNotification.AllAvailableFreeTickets > 1 ? "Free Tickets" : "Free Ticket"}
          </span>
          <span className="text-[12px] text-[#454553] relative">
            {userData?.FreeTicketNotification.AllAvailableFreeTickets}
            <span className="absolute w-[12px] h-[12px] rounded-full top-[-3px] left-0"></span>
          </span>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 4L10 8L6 12" stroke="#FF8200" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      )}
    </div>
  );

  const getDesktopView = () => (
    <div className="relative">
      <div
        ref={ref}
        className="flex gap-2 p-2 border border-solid border-[#e0e0e0] rounded-full justify-center items-center cursor-pointer">
        <div className="flex items-center gap-1 relative">
          <Link
            aria-disabled={doesUserNotHaveFreeTickets}
            tabIndex={doesUserNotHaveFreeTickets ? -1 : undefined}
            className={clsx(doesUserNotHaveFreeTickets && "pointer-events-none")}
            href="/account/my-wallet">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M15.7992 6.3104V3.2C15.7992 2.537 15.2622 2 14.5992 2H1.39922C0.736219 2 0.199219 2.537 0.199219 3.2V6.3104C0.897019 6.5582 1.39922 7.2176 1.39922 8C1.39922 8.7824 0.897019 9.4418 0.199219 9.6896V12.8C0.199219 13.463 0.736219 14 1.39922 14H14.5992C15.2622 14 15.7992 13.463 15.7992 12.8V9.6896C15.1014 9.4418 14.5992 8.7824 14.5992 8C14.5992 7.2176 15.1014 6.5582 15.7992 6.3104ZM11.3304 7.3766L9.58922 8.6726L10.236 10.7444C10.323 11.0222 10.0074 11.2514 9.76982 11.0828L7.99922 9.8276L6.22862 11.0828C5.99102 11.2514 5.67602 11.0222 5.76242 10.7444L6.40922 8.6726L4.66802 7.3766C4.43462 7.2032 4.55522 6.8324 4.84622 6.8288L7.01642 6.8036L7.71122 4.7474C7.80422 4.472 8.19422 4.472 8.28722 4.7474L8.98202 6.8036L11.1522 6.8288C11.4432 6.8318 11.5638 7.2026 11.3304 7.3766Z"
                fill={doesUserNotHaveFreeTickets ? "#DADADA" : "#FF8200"}
              />
            </svg>
            {userData?.FreeTicketNotification.AllAvailableFreeTickets > 0 && (
              <span className="absolute bg-[#ff0000] w-[12px] h-[12px] rounded-full top-[-3px] right-[-5px]"></span>
            )}
          </Link>
        </div>
        <div className="w-[0.1rem] h-4 border border-solid border-[#e0e0e0]" />
        {userData?.UserDetails !== null && (
          <span
            className="flex items-center gap-1"
            onClick={() => {
              setArrowState(prev => (prev === "down" ? "up" : "down"));
              setIsUserDetailsPopupOpen(arrowState === "down");
            }}>
            <img src="/assets/images/money-icon.svg" alt="money-icon" width={16} height={16} />
            <span className="text-[12px] font-normal text-[#4c4c52]">
              {userData?.UserDetails.FormattedTotalGameCredits}
            </span>
          </span>
        )}
        <svg
          onClick={() => {
            setArrowState(prev => (prev === "down" ? "up" : "down"));
            setIsUserDetailsPopupOpen(arrowState === "down");
          }}
          className={`${arrowStyle[arrowState]} transition-all`}
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg">
          <path d="M12 6L8 10L4 6" stroke="#898994" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      {isUserDetailsPopupOpen && getUserGameCreditsAndFreeTickets()}
    </div>
  );

  const getMobileView = () => {
    const freeTicketText =
      userData?.FreeTicketNotification.AllAvailableFreeTickets > 1 ? "Free Tickets" : "Free Ticket";
    return (
      <div className="flex flex-col gap-2 relative overflow-hidden">
        <div className="flex justify-between">
          <div className="flex flex-col gap-2">
            <h5 className="font-jost text-[#28282b] text-[16px] font-medium">Hello {userData?.FirstName},</h5>
            <span className="text-[#696971] text-[12px] font-normal">You have:</span>
          </div>
          {userData?.TierImageUrl && <img src={userData?.TierImageUrl} alt="user-tier-image" width={25} height={25} />}
        </div>
        <hr />
        <div className="flex justify-between py-2">
          <div className="flex gap-2 items-center">
            <img src="/assets/images/money-icon.svg" alt="money-icon" width={16} height={16} />
            <span className="text-[14px] text-[#4c4c52] font-normal">Game Credit:</span>
            <span className="text-[14px] text-[#ff8200] font-semibold">
              {userData?.UserDetails.FormattedTotalGameCredits}
            </span>
          </div>
          <div className="flex items-center gap-2 cursor-pointer hover:underline" onClick={onFreeTicketsClick}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M15.7992 6.3104V3.2C15.7992 2.537 15.2622 2 14.5992 2H1.39922C0.736219 2 0.199219 2.537 0.199219 3.2V6.3104C0.897019 6.5582 1.39922 7.2176 1.39922 8C1.39922 8.7824 0.897019 9.4418 0.199219 9.6896V12.8C0.199219 13.463 0.736219 14 1.39922 14H14.5992C15.2622 14 15.7992 13.463 15.7992 12.8V9.6896C15.1014 9.4418 14.5992 8.7824 14.5992 8C14.5992 7.2176 15.1014 6.5582 15.7992 6.3104ZM11.3304 7.3766L9.58922 8.6726L10.236 10.7444C10.323 11.0222 10.0074 11.2514 9.76982 11.0828L7.99922 9.8276L6.22862 11.0828C5.99102 11.2514 5.67602 11.0222 5.76242 10.7444L6.40922 8.6726L4.66802 7.3766C4.43462 7.2032 4.55522 6.8324 4.84622 6.8288L7.01642 6.8036L7.71122 4.7474C7.80422 4.472 8.19422 4.472 8.28722 4.7474L8.98202 6.8036L11.1522 6.8288C11.4432 6.8318 11.5638 7.2026 11.3304 7.3766Z"
                fill={doesUserNotHaveFreeTickets ? "#DADADA" : "#FF8200"}
              />
            </svg>
            <span className="text-[14px] font-normal text-[#4c4c52]">
              {freeTicketText}
              <span className="relative mr-4 ml-2 text-[#ff8200]">
                {userData?.FreeTicketNotification.AllAvailableFreeTickets}
                {userData?.FreeTicketNotification.AllAvailableFreeTickets > 0 && (
                  <span className="absolute w-[12px] h-[12px] rounded-full top-[-3px] left-0"></span>
                )}
              </span>
            </span>
          </div>
        </div>
        <svg
          className="absolute top-0 opacity-40 right-[-35px]"
          width="79"
          height="110"
          viewBox="0 0 79 110"
          fill="none"
          xmlns="http://www.w3.org/2000/svg">
          <path
            opacity="0.21"
            d="M413 -14.1726C413 99.8918 320.564 192.327 206.5 192.327C92.4356 192.327 0 99.8918 0 -14.1726"
            fill="#FF8200"
          />
        </svg>
        <svg
          className="absolute top-0 opacity-20 right-[-4px]"
          width="79"
          height="110"
          viewBox="0 0 79 110"
          fill="none"
          xmlns="http://www.w3.org/2000/svg">
          <path
            opacity="0.21"
            d="M413 -14.1726C413 99.8918 320.564 192.327 206.5 192.327C92.4356 192.327 0 99.8918 0 -14.1726"
            fill="#FF8200"
          />
        </svg>
      </div>
    );
  };

  return isDesktop ? getDesktopView() : getMobileView();
};

export default UserRewardDetails;
